import { memo, useMemo } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { SDropdownMenu } from '@setvi/shared/components';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { DeleteIconV2, EditIcon } from '@setvi/shared/components/sicons';
import { COLUMN_WIDTHS } from '../column-widths';
import { ProductInfo } from './product-info';
import { QuantityColumn, SellColumn, CostColumn, GpColumn } from './columns';

const useStyles = makeStyles(() => ({
  optionRowWrapper: {
    position: 'relative',
    paddingLeft: '61px'
  },
  connectingLineContainer: {
    position: 'absolute',
    left: '33px',
    top: 0,
    bottom: 0,
    width: '31px'
  },
  verticalLine: {
    position: 'absolute',
    left: 0,
    top: '-70px',
    width: '1px',
    height: 'calc(100% + 100px)',
    background:
      'repeating-linear-gradient(to bottom, #AFBAC6 0, #AFBAC6 5px, transparent 4px, transparent 11px)'
  },
  verticalLineLastChild: {
    position: 'absolute',
    left: 0,
    top: '-50px',
    width: '1px',
    height: '90px',
    background:
      'repeating-linear-gradient(to bottom, #AFBAC6 0, #AFBAC6 5px, transparent 4px, transparent 11px)'
  },
  cornerPiece: {
    position: 'absolute',
    left: 0,
    top: '37px',
    width: '7px',
    height: '7px',
    borderLeft: '1px solid #AFBAC6',
    borderBottom: '1px solid #AFBAC6',
    borderBottomLeftRadius: '5px'
  },
  horizontalConnector: {
    position: 'absolute',
    left: '11px',
    top: '42px',
    width: '25px',
    height: '1px',
    background:
      'repeating-linear-gradient(to right, #AFBAC6 0, #AFBAC6 5px, transparent 5px, transparent 11px)'
  },
  rowContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '12px 0',
    position: 'relative',
    width: 'fit-content',
    minWidth: '100%'
  },
  productContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: '16px',
    minHeight: '86px'
  },
  productInfo: {
    minWidth: COLUMN_WIDTHS.productInfo,
    flex: 1
  },
  qtyColumn: {
    width: COLUMN_WIDTHS.qty,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  unitPriceColumn: {
    width: COLUMN_WIDTHS.unitPrice,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  gpColumn: {
    width: COLUMN_WIDTHS.gp,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  totalPriceColumn: {
    width: COLUMN_WIDTHS.totalPrice,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  actionColumn: {
    width: COLUMN_WIDTHS.action,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

/**
 * Handlers passed from parent to avoid context subscriptions in each row
 */
export interface OptionRowHandlers {
  onQuantityChange: (productId: string, value: number) => void;
  onSellChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onProfitMarginChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onProfitMarginPercentageChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onEditOption: (product: QuoteProductDTO) => void;
  onRemoveOption: (productId: string) => void;
}

interface OptionRowProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  isSectionChild?: boolean;
  onProductClick?: (product: QuoteProductDTO) => void;
  isLastChild?: boolean;
  isQuoteEditable: boolean;
  handlers: OptionRowHandlers;
}

/**
 * OptionRow - Lightweight presentational component for option rows
 *
 * This component does NOT subscribe to any contexts to minimize memory usage.
 * All handlers and state are passed down from the parent ProductRow.
 */
export const OptionRow = memo(
  ({
    product,
    searchQuery,
    isSectionChild,
    onProductClick,
    isLastChild = false,
    isQuoteEditable,
    handlers
  }: OptionRowProps) => {
    const classes = useStyles();
    const productId = product?.id || product?._id;
    const notEditableTooltip = 'Quote is not editable';

    const menuItems = useMemo(
      () =>
        !isQuoteEditable
          ? []
          : [
              {
                color: '#333D47',
                icon: <EditIcon fontSize="small" />,
                text: 'Edit Option Details',
                onClick: () => handlers.onEditOption(product)
              },
              {
                icon: <DeleteIconV2 fontSize="small" />,
                color: 'danger',
                text: 'Remove Option',
                onClick: () => handlers.onRemoveOption(productId)
              }
            ],
      [isQuoteEditable, handlers, product, productId]
    );

    return (
      <Box className={classes.optionRowWrapper}>
        <Box className={classes.connectingLineContainer}>
          <Box
            className={
              isLastChild ? classes.verticalLineLastChild : classes.verticalLine
            }
          />
          <Box className={classes.cornerPiece} />
          <Box className={classes.horizontalConnector} />
        </Box>
        <Box className={classes.rowContainer}>
          <Box className={classes.productContainer}>
            <Box className={classes.productInfo}>
              <ProductInfo
                product={product}
                searchQuery={searchQuery}
                onClick={
                  onProductClick ? () => onProductClick(product) : undefined
                }
                badgeText={product?.labelType}
              />
            </Box>

            <Box className={classes.qtyColumn}>
              <QuantityColumn
                product={product}
                productId={productId}
                onQuantityChange={handlers.onQuantityChange}
                disabled={!isQuoteEditable}
                disabledTooltip={notEditableTooltip}
              />
            </Box>

            <Box className={classes.unitPriceColumn}>
              <SellColumn
                product={product}
                productId={productId}
                onSellChange={handlers.onSellChange}
                disabled={!isQuoteEditable}
                disabledTooltip={notEditableTooltip}
              />
            </Box>

            <Box className={classes.gpColumn}>
              <GpColumn
                product={product}
                productId={productId}
                onProfitMarginChange={handlers.onProfitMarginChange}
                onProfitMarginPercentageChange={
                  handlers.onProfitMarginPercentageChange
                }
                disabled={!isQuoteEditable}
                disabledTooltip={notEditableTooltip}
              />
            </Box>

            <Box className={classes.totalPriceColumn}>
              <CostColumn product={product} />
            </Box>

            <Box
              className={classes.actionColumn}
              style={
                isSectionChild
                  ? { width: `calc(${COLUMN_WIDTHS.action} - 12px)` }
                  : undefined
              }>
              <SDropdownMenu
                newDesign
                disabled={!isQuoteEditable}
                disabledTooltip={notEditableTooltip}
                menuItems={menuItems}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);
